import React, { useState, useEffect, useCallback } from 'react';
import { reportService, productService, extractErrorDetails } from '../services/api';
import ErrorDisplay from './ErrorDisplay';

// Color palette for supermercado lines
const LINE_COLORS = [
  '#e53935', // red
  '#8e24aa', // purple
  '#43a047', // green
  '#fb8c00', // orange
  '#00acc1', // cyan
  '#5e35b1', // deep purple
  '#d81b60', // pink
  '#039be5', // light blue
  '#7cb342', // light green
  '#f4511e', // deep orange
];

function ProductFlowReport() {
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [productsLoading, setProductsLoading] = useState(true);
  const [error, setError] = useState('');
  const [errorTrace, setErrorTrace] = useState(null);
  
  const [filters, setFilters] = useState({
    fromDate: '',
    toDate: ''
  });
  
  const [productSearch, setProductSearch] = useState('');
  const [filteredProducts, setFilteredProducts] = useState([]);

  // Load products on mount
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await productService.getAllProducts();
        if (response.success) {
          setProducts(response.data || []);
        }
      } catch (err) {
        console.error('Error loading products:', err);
      } finally {
        setProductsLoading(false);
      }
    };
    fetchProducts();
  }, []);

  // Filter products based on search
  useEffect(() => {
    if (productSearch.trim() === '') {
      setFilteredProducts([]);
    } else {
      const query = productSearch.toLowerCase();
      const filtered = products.filter(p =>
        p.codigoBarras.toLowerCase().includes(query) ||
        p.nombre.toLowerCase().includes(query)
      );
      setFilteredProducts(filtered.slice(0, 10));
    }
  }, [productSearch, products]);

  const fetchReport = useCallback(async () => {
    if (!selectedProduct) return;
    
    try {
      setLoading(true);
      setError('');
      setErrorTrace(null);
      
      const response = await reportService.getProductFlowReport(
        selectedProduct.id,
        filters.fromDate || null,
        filters.toDate || null
      );
      
      if (response.success) {
        setReportData(response.data);
      } else {
        setError(response.message || 'Error al cargar el reporte');
        setErrorTrace(response.errorTrace || null);
      }
    } catch (err) {
      const { message, errorTrace: trace } = extractErrorDetails(err, 'Error al cargar el reporte de flujo');
      setError(message);
      setErrorTrace(trace);
    } finally {
      setLoading(false);
    }
  }, [selectedProduct, filters]);

  useEffect(() => {
    if (selectedProduct) {
      fetchReport();
    }
  }, [selectedProduct, fetchReport]);

  const handleProductSelect = (product) => {
    setSelectedProduct(product);
    setProductSearch(product.nombre);
    setFilteredProducts([]);
    
    // Set default date range to last 30 days
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    
    setFilters({
      fromDate: thirtyDaysAgo.toISOString().split('T')[0],
      toDate: today.toISOString().split('T')[0]
    });
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleClearProduct = () => {
    setSelectedProduct(null);
    setProductSearch('');
    setReportData(null);
    setFilters({ fromDate: '', toDate: '' });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString + 'T00:00:00').toLocaleDateString('es-UY', { 
      day: '2-digit',
      month: '2-digit'
    });
  };

  // Calculate max value for chart scaling
  const getMaxValue = () => {
    if (!reportData || !reportData.dailyData) return 0;
    
    let max = 0;
    reportData.dailyData.forEach(day => {
      max = Math.max(max, day.entradas || 0);
      if (day.salidasPorSupermercado) {
        Object.values(day.salidasPorSupermercado).forEach(val => {
          max = Math.max(max, val);
        });
      }
    });
    return max;
  };

  const maxValue = getMaxValue();

  // Get color for a supermercado
  const getSupermercadoColor = (index) => {
    return LINE_COLORS[index % LINE_COLORS.length];
  };

  // Calculate totals
  const getTotals = () => {
    if (!reportData || !reportData.dailyData) return { entradas: 0, salidas: {} };
    
    let totalEntradas = 0;
    const salidasPorSupermercado = {};
    
    reportData.dailyData.forEach(day => {
      totalEntradas += day.entradas || 0;
      if (day.salidasPorSupermercado) {
        Object.entries(day.salidasPorSupermercado).forEach(([supermercado, cantidad]) => {
          salidasPorSupermercado[supermercado] = (salidasPorSupermercado[supermercado] || 0) + cantidad;
        });
      }
    });
    
    return { entradas: totalEntradas, salidas: salidasPorSupermercado };
  };

  const totals = getTotals();

  return (
    <div className="report-container">
      <h2>Reporte de Flujo de Producto</h2>
      <p className="report-description">
        Seleccione un artículo y un rango de fechas para ver las cantidades que entraron al depósito 
        y las cantidades enviadas a cada supermercado por día.
      </p>
      
      <div className="report-filters">
        <div className="filter-row">
          <div className="filter-group" style={{ flex: 2 }}>
            <label htmlFor="productSearch">Producto</label>
            <div className="product-search">
              <input
                type="text"
                id="productSearch"
                value={productSearch}
                onChange={(e) => {
                  setProductSearch(e.target.value);
                  if (e.target.value === '') {
                    handleClearProduct();
                  }
                }}
                placeholder={productsLoading ? "Cargando productos..." : "Buscar producto por nombre o código..."}
                disabled={productsLoading}
              />
              {filteredProducts.length > 0 && (
                <ul className="search-results">
                  {filteredProducts.map(product => (
                    <li key={product.id} onClick={() => handleProductSelect(product)}>
                      <span className="product-code">{product.codigoBarras}</span>
                      <span className="product-name">{product.nombre}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
        
        {selectedProduct && (
          <div className="filter-row">
            <div className="filter-group">
              <label htmlFor="fromDate">Desde</label>
              <input
                type="date"
                id="fromDate"
                value={filters.fromDate}
                onChange={(e) => handleFilterChange('fromDate', e.target.value)}
              />
            </div>
            
            <div className="filter-group">
              <label htmlFor="toDate">Hasta</label>
              <input
                type="date"
                id="toDate"
                value={filters.toDate}
                onChange={(e) => handleFilterChange('toDate', e.target.value)}
              />
            </div>
            
            <div className="filter-group" style={{ justifyContent: 'flex-end' }}>
              <button onClick={handleClearProduct} className="btn btn-secondary">
                Limpiar Selección
              </button>
            </div>
          </div>
        )}
        {selectedProduct && reportData && (
          <div className="export-buttons" style={{ marginTop: '10px' }}>
            <button
              onClick={async () => {
                try {
                  await reportService.exportProductFlowToPdf(
                    selectedProduct.id,
                    filters.fromDate || null,
                    filters.toDate || null
                  );
                } catch (err) {
                  console.error('Error exporting to PDF:', err);
                  alert('Error al exportar a PDF');
                }
              }}
              className="btn btn-small btn-primary"
              style={{ marginRight: '10px' }}
            >
              📄 Exportar a PDF
            </button>
            <button
              onClick={async () => {
                try {
                  await reportService.exportProductFlowToExcel(
                    selectedProduct.id,
                    filters.fromDate || null,
                    filters.toDate || null
                  );
                } catch (err) {
                  console.error('Error exporting to Excel:', err);
                  alert('Error al exportar a Excel');
                }
              }}
              className="btn btn-small btn-success"
              style={{ marginRight: '10px' }}
            >
              📊 Exportar a Excel
            </button>
            <button
              onClick={async () => {
                try {
                  await reportService.exportProductFlowToCsv(
                    selectedProduct.id,
                    filters.fromDate || null,
                    filters.toDate || null
                  );
                } catch (err) {
                  console.error('Error exporting to CSV:', err);
                  alert('Error al exportar a CSV');
                }
              }}
              className="btn btn-small btn-info"
            >
              📋 Exportar a CSV
            </button>
          </div>
        )}
      </div>

      <ErrorDisplay error={error} errorTrace={errorTrace} />

      {!selectedProduct ? (
        <div className="empty-state">
          <p>Seleccione un producto para ver su reporte de flujo</p>
        </div>
      ) : loading ? (
        <p className="loading-text">Cargando reporte...</p>
      ) : reportData && reportData.dailyData && reportData.dailyData.length > 0 ? (
        <div className="flow-report-content">
          {/* Product info */}
          <div className="report-product-info">
            <h3>{reportData.productName}</h3>
            <span className="product-barcode">Código: {reportData.productBarcode}</span>
          </div>
          
          {/* Legend */}
          <div className="flow-chart-legend">
            <div className="legend-item">
              <span className="legend-color legend-entradas"></span>
              <span className="legend-label">Entradas al Depósito</span>
            </div>
            {reportData.supermercados && reportData.supermercados.map((supermercado, index) => (
              <div key={supermercado} className="legend-item">
                <span 
                  className="legend-color" 
                  style={{ backgroundColor: getSupermercadoColor(index) }}
                ></span>
                <span className="legend-label">{supermercado}</span>
              </div>
            ))}
          </div>
          
          {/* Chart */}
          <div className="flow-chart-container">
            <div className="flow-chart">
              {reportData.dailyData.map((day, dayIndex) => (
                <div key={dayIndex} className="flow-chart-day">
                  <div className="flow-chart-bars">
                    {/* Entrada bar */}
                    <div className="flow-bar-wrapper">
                      <div 
                        className="flow-bar flow-bar-entrada"
                        style={{ 
                          height: maxValue > 0 ? `${(day.entradas / maxValue) * 100}%` : '0%',
                          minHeight: day.entradas > 0 ? '10px' : '0'
                        }}
                        title={`Entradas: ${day.entradas || 0}`}
                      >
                        {day.entradas > 0 && (
                          <span className="bar-value">{day.entradas}</span>
                        )}
                      </div>
                    </div>
                    
                    {/* Supermercado bars */}
                    {reportData.supermercados && reportData.supermercados.map((supermercado, supIndex) => {
                      const cantidad = day.salidasPorSupermercado?.[supermercado] || 0;
                      return (
                        <div key={supermercado} className="flow-bar-wrapper">
                          <div 
                            className="flow-bar"
                            style={{ 
                              height: maxValue > 0 ? `${(cantidad / maxValue) * 100}%` : '0%',
                              minHeight: cantidad > 0 ? '10px' : '0',
                              backgroundColor: getSupermercadoColor(supIndex)
                            }}
                            title={`${supermercado}: ${cantidad}`}
                          >
                            {cantidad > 0 && (
                              <span className="bar-value">{cantidad}</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <span className="flow-chart-date">{formatDate(day.date)}</span>
                </div>
              ))}
            </div>
          </div>
          
          {/* Summary */}
          <div className="flow-chart-summary">
            <h4>Resumen del Período</h4>
            <div className="summary-grid">
              <div className="summary-item summary-entradas">
                <span className="summary-label">Total Entradas:</span>
                <span className="summary-value">{totals.entradas}</span>
              </div>
              {reportData.supermercados && reportData.supermercados.map((supermercado, index) => (
                <div key={supermercado} className="summary-item">
                  <span className="summary-label" style={{ color: getSupermercadoColor(index) }}>
                    {supermercado}:
                  </span>
                  <span className="summary-value">{totals.salidas[supermercado] || 0}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="empty-state">
          <p>No hay datos de movimientos para el producto y período seleccionados</p>
        </div>
      )}
    </div>
  );
}

export default ProductFlowReport;
